import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import WorkMirrorConstellationCard from './WorkMirrorConstellationCard'
import WorkMirrorBookDetailsCard from './WorkMirrorBookDetailsCard'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectAnalyticDetailsStateAnalyticDetails,
  selectAnalyticDetailsStateAnalyticDetailsBooksInputValues,
  selectAnalyticDetailsStateGetBookRecoV3Error,
  selectAnalyticDetailsStateGetBookRecoV3Success,
  selectAnalyticDetailsStateWorkMirrorBookDetailsCardValues,
} from '../../../../store/getters/analytics-details/analytic-details-v2.getters'
import {
  analyticDetailsV2StateAddBookInputAndGetBookRecoV3Action,
  analyticDetailsV2StateGetBookDetailsData,
  analyticDetailsV2StateRemoveBookInputAndGetBookRecoV3Action,
} from '../../../../store/actions/analytics-details/analytic-details-v2.actions'
import { setShowSnackBar } from '../../../../store/reducers/web-app.reducer'
import WorkMirrorListCard from './WorkMirrorListCard'
import { resetGetBookRecoV3Status } from '../../../../store/reducers/analytics-details/analytic-details-v2.reducer'
import { WorkMirrorBookInterface } from '../../../../core/interface/report/work-mirror-book.interface'
import {
  buildRelativePath,
  PathConfig,
} from '../../../../core/config/PathConfig'
import GlDateManager from '../../../share/GlDateManager'

const AnalyticDetailsWorkMirrorView = ({
  handleSaveAnalytics,
  handleShareAnalytics,
}: {
  handleSaveAnalytics: () => void
  handleShareAnalytics: () => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [openListCard, setOpenListCard] = useState<boolean>(false)
  const { reportId: urlReportId } = useParams<{ reportId: string }>()
  const [searchParams] = useSearchParams()

  const dispatch = useAppDispatch()

  const getBookRecoV3Success = useAppSelector(
    selectAnalyticDetailsStateGetBookRecoV3Success,
  )
  const getBookRecoV3Error = useAppSelector(
    selectAnalyticDetailsStateGetBookRecoV3Error,
  )

  const inputBookValues = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetailsBooksInputValues,
  )

  const analyticsDetails = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetails,
  )

  const {
    isLoadingBookDetailsGetBookReco,
    bookDetailsBookReco,
    isLoadingGetBookDetails,
    isLoadingGetEditions,
    isLoadingGetB2BBookDetails,
    bookDetailsSelected,
    bookDetailsSelectedEditions,
    bookDetailsSelectedB2BInfo,
  } = useAppSelector(selectAnalyticDetailsStateWorkMirrorBookDetailsCardValues)
  const handleOpenListView = () => {
    setOpenListCard(!openListCard)
  }

  const handleAddBookToAnalytic = (eanId: number) => {
    if (inputBookValues.includes(`${eanId}`)) {
      dispatch(setShowSnackBar(t('workMirrorAlreadySelected')))
    } else if (inputBookValues.length < 3)
      if (urlReportId === 'new') {
        const eans = searchParams.get('eans') as string
        let eanIds = []
        if (eans) {
          eanIds = [...`${eans}`.split(','), `${eanId}`]
        } else {
          eanIds.push(eanId)
        }

        const popularCatalogClilNameFilter = searchParams.get(
          'popular_catalog_clil_name_filter',
        ) as string

        navigate(
          buildRelativePath([
            PathConfig.BOOKMETRIE,
            PathConfig.ANALYTICS,
            `new?type=${analyticsDetails.type}&eans=${eanIds.join(',')}${
              popularCatalogClilNameFilter
                ? `&popular_catalog_clil_name_filter=${popularCatalogClilNameFilter}`
                : ''
            }`,
          ]),
        )
      } else {
        dispatch(
          analyticDetailsV2StateAddBookInputAndGetBookRecoV3Action({ eanId }),
        ).finally(() => {
          dispatch(resetGetBookRecoV3Status())
        })
      }
  }

  const handleRemoveBookInput = (eanId: number) => {
    if (urlReportId === 'new') {
      const eans = searchParams.get('eans') as string
      let eanIds: string[] = []
      if (eans) {
        eanIds = [...`${eans}`.split(',')].filter(
          (ean: string) => ean !== `${eanId}`,
        )
      }

      const popularCatalogClilNameFilter = searchParams.get(
        'popular_catalog_clil_name_filter',
      ) as string

      navigate(
        buildRelativePath([
          PathConfig.BOOKMETRIE,
          PathConfig.ANALYTICS,
          `new?type=${analyticsDetails.type}${
            eanIds.length ? `&eans=${eanIds.join(',')}` : ''
          }${
            popularCatalogClilNameFilter
              ? `&popular_catalog_clil_name_filter=${popularCatalogClilNameFilter}`
              : ''
          }`,
        ]),
      )
    } else {
      dispatch(
        analyticDetailsV2StateRemoveBookInputAndGetBookRecoV3Action({ eanId }),
      ).finally(() => {
        dispatch(resetGetBookRecoV3Status())
      })
    }
  }

  const handleCloseBookDetails = useCallback(() => {
    void dispatch(
      analyticDetailsV2StateGetBookDetailsData({ bookDetails: null }),
    )
  }, [bookDetailsSelected])

  const handleCloseList = () => {
    setOpenListCard(false)
  }

  useEffect(() => {
    if (getBookRecoV3Error) {
      // dispatch(setShowSnackBar(t('snackbarMessage.errorMessage')))
    }
  }, [getBookRecoV3Success, getBookRecoV3Error])

  const [bookEanIdHovered, setBookEanIdHovered] = useState<number | null>(null)
  const handleBookHovered = useCallback(
    (eanId: number | null) => {
      setBookEanIdHovered(eanId)
    },
    [bookEanIdHovered],
  )

  const handleBookSelected = useCallback(
    (bookDetails: WorkMirrorBookInterface | null) => {
      void dispatch(analyticDetailsV2StateGetBookDetailsData({ bookDetails }))
    },
    [
      bookDetailsSelected,
      bookDetailsSelectedEditions,
      bookDetailsSelectedB2BInfo,
    ],
  )

  return (
    <div className="flex-auto flex flex-col p-8 gap-2">
      <GlDateManager
        prefix={t('ReportReaderShipProfileDate')}
        date={analyticsDetails.modified}
      />
      <div className="flex-auto flex flex-row gap-2">
        <WorkMirrorConstellationCard
          openListCard={openListCard}
          handleOpenListView={handleOpenListView}
          handleSaveAnalytics={handleSaveAnalytics}
          handleShareAnalytics={handleShareAnalytics}
          handleAddBookToAnalytic={handleAddBookToAnalytic}
          handleRemoveBookInput={handleRemoveBookInput}
          handleBookHovered={handleBookHovered}
          bookDetailsSelected={bookDetailsSelected}
          handleBookSelected={handleBookSelected}
        />
        {(bookDetailsSelected && (
          <WorkMirrorBookDetailsCard
            isLoadingBookDetailsGetBookReco={isLoadingBookDetailsGetBookReco}
            bookDetailsBookReco={bookDetailsBookReco}
            isLoadingGetBookDetails={isLoadingGetBookDetails}
            isLoadingGetEditions={isLoadingGetEditions}
            isLoadingGetB2BBookDetails={isLoadingGetB2BBookDetails}
            openListCard={openListCard}
            bookDetails={bookDetailsSelected}
            bookDetailsSelectedEditions={bookDetailsSelectedEditions}
            bookDetailsSelectedB2BInfo={bookDetailsSelectedB2BInfo}
            handleCloseBookDetails={handleCloseBookDetails}
          />
        )) ||
          (openListCard && (
            <WorkMirrorListCard
              bookEanIdHovered={bookEanIdHovered}
              handleCloseList={handleCloseList}
            />
          )) ||
          null}
      </div>
    </div>
  )
}

export default AnalyticDetailsWorkMirrorView
